import { eur } from "@/lib/format";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { CategoryIcon, lookupCategory } from "@/lib/categoryIcons";
import { useLongPress } from "@/lib/useLongPress";
import { accountLabel } from "@/lib/accountScope";
import { ArrowsLeftRight, Repeat } from "@phosphor-icons/react";

/**
 * The phone rendering of the ledger. The desktop table puts seven columns on
 * one row; at 375px that pushes the amount — the thing anyone actually scans
 * for — off the right edge. A card keeps date, description and category on the
 * left and the signed amount hard right, so the column that matters is always
 * the first thing in view.
 *
 * Tapping a card edits it. There is no room for a permanent checkbox column,
 * so bulk selection arrives by holding a row instead; see `useLongPress`.
 */
function TransactionCard({
  transaction, category, accountsById, selected, selectionMode, onEdit, onToggle, onEnterSelection,
}) {
  const press = useLongPress(
    () => onEnterSelection(transaction.id),
    () => (selectionMode ? onToggle(transaction.id, !selected) : onEdit(transaction)),
  );
  const income = transaction.type === "income";

  return (
    <div
      role="button"
      tabIndex={0}
      aria-pressed={selectionMode ? selected : undefined}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          if (selectionMode) onToggle(transaction.id, !selected);
          else onEdit(transaction);
        }
      }}
      {...press}
      data-testid={`txn-card-${transaction.id}`}
      className={`flex w-full items-center gap-3 border-b border-border px-4 py-3 text-left select-none last:border-b-0 ${
        selected ? "bg-expense/[0.07]" : "active:bg-secondary/40"
      }`}
    >
      {selectionMode && (
        <Checkbox
          checked={selected}
          onCheckedChange={(checked) => onToggle(transaction.id, Boolean(checked))}
          onClick={(event) => event.stopPropagation()}
          aria-label={`Select transaction from ${transaction.date}`}
          data-testid={`select-card-${transaction.id}`}
          className="shrink-0"
        />
      )}

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          {transaction.recurring && <Repeat size={13} className="shrink-0 text-forecast" />}
          <p className="truncate text-sm font-medium">
            {transaction.description || <span className="text-muted-foreground">No description</span>}
          </p>
        </div>
        <div className="mt-1.5 flex items-center gap-2">
          {transaction.transfer_id ? (
            <Badge variant="outline" className="gap-1 border-border pl-1.5 text-[11px] font-normal text-forecast">
              <ArrowsLeftRight size={11} />
              <span className="text-foreground">Transfer</span>
            </Badge>
          ) : (
            <Badge
              variant="outline"
              className="gap-1 border-border pl-1.5 text-[11px] font-normal"
              style={category ? { color: category.color, borderColor: `${category.color}59` } : undefined}
            >
              <CategoryIcon category={category} size={11} />
              <span className="max-w-[9rem] truncate text-foreground">{transaction.category}</span>
            </Badge>
          )}
          <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{transaction.date}</span>
          {accountsById && (
            <span className="min-w-0 truncate text-xs text-muted-foreground">
              {accountLabel(transaction, accountsById)}
            </span>
          )}
        </div>
      </div>

      <p className={`shrink-0 text-sm font-semibold tabular-nums ${income ? "text-income" : "text-expense"}`}>
        {income ? "+" : "−"}{eur(transaction.amount)}
      </p>
    </div>
  );
}

export default function TransactionList({
  transactions, categoryIndex: catIndex, accountsById = null, selectedIds, selectionMode,
  onEdit, onToggle, onEnterSelection,
}) {
  return (
    <div className="md:hidden" data-testid="transactions-card-list">
      {selectionMode && (
        <p className="border-b border-border px-4 py-2 text-xs text-muted-foreground">
          Tap to select · hold a row to start selecting
        </p>
      )}
      {transactions.map((transaction) => (
        <TransactionCard
          key={transaction.id}
          transaction={transaction}
          category={lookupCategory(catIndex, transaction.type, transaction.category)}
          accountsById={accountsById}
          selected={selectedIds.has(transaction.id)}
          selectionMode={selectionMode}
          onEdit={onEdit}
          onToggle={onToggle}
          onEnterSelection={onEnterSelection}
        />
      ))}
    </div>
  );
}
